/**
 * Stats Cards Component
 * Displays key financial metrics in a grid of cards
 */
import Card from '@/components/ui/Card';
import { formatCurrency } from '@/utils/format';
import { cn } from '@/utils/cn';
import type { DashboardStats } from '@/hooks/useDashboardData';

interface StatsCardsProps {
  stats: DashboardStats;
  isLoading?: boolean;
}

interface StatCardProps {
  label: string;
  value: number;
  subtext?: string;
  valueClassName?: string;
  isLoading?: boolean;
}

function StatCard({ label, value, subtext, valueClassName, isLoading }: StatCardProps) {
  // Ensure value is a valid number for formatting
  const displayValue = Number.isFinite(value) ? value : 0;

  return (
    <Card>
      <div className="text-sm font-medium text-gray-500">{label}</div>
      {isLoading ? (
        <div className="mt-1 h-8 w-32 animate-pulse rounded bg-gray-200" />
      ) : (
        <div className={cn('mt-1 text-2xl font-semibold', valueClassName || 'text-gray-900')}>
          {formatCurrency(displayValue)}
        </div>
      )}
      {subtext && !isLoading && <div className="mt-1 text-sm text-gray-500">{subtext}</div>}
      {isLoading && <div className="mt-1 h-4 w-20 animate-pulse rounded bg-gray-100" />}
    </Card>
  );
}

export default function StatsCards({ stats, isLoading }: StatsCardsProps) {
  // Safely extract values with defaults to handle potential undefined stats
  // WP-DASH-1: Using rolling 30-day window instead of calendar month
  const totalBalance = Number(stats?.totalBalance) || 0;
  const incomeLast30Days = Number(stats?.incomeLast30Days) || 0;
  const expensesLast30Days = Number(stats?.expensesLast30Days) || 0;
  const netLast30Days = Number(stats?.netLast30Days) || 0;
  const accountsCount = Number(stats?.accountsCount) || 0;

  const savingsRate =
    incomeLast30Days > 0
      ? Math.round((netLast30Days / incomeLast30Days) * 100)
      : 0;

  const netSubtext =
    netLast30Days >= 0
      ? savingsRate > 0
        ? `Saving ${savingsRate}% of income`
        : 'Breaking even'
      : `Spending ${Math.abs(savingsRate)}% more than income`;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mb-8">
      <StatCard
        label="Total Balance"
        value={totalBalance}
        subtext={`${accountsCount} account${accountsCount !== 1 ? 's' : ''}`}
        isLoading={isLoading}
      />
      <StatCard
        label="Income (30 Days)"
        value={incomeLast30Days}
        subtext="Last 30 days"
        valueClassName="text-flow-income"
        isLoading={isLoading}
      />
      <StatCard
        label="Expenses (30 Days)"
        value={expensesLast30Days}
        subtext="Last 30 days"
        valueClassName="text-flow-expense"
        isLoading={isLoading}
      />
      <StatCard
        label="Net (30 Days)"
        value={netLast30Days}
        subtext={netSubtext}
        valueClassName={netLast30Days >= 0 ? 'text-flow-income' : 'text-flow-expense'}
        isLoading={isLoading}
      />
    </div>
  );
}
